import { ROUTE_EXAMPLE, VENUES } from "../landingContent";

const QUOTE_COLUMNS = ["Venue", "Entry", "Fee", "Impact", "Net entry"];

/**
 * Best-execution story: USDX does not run its own book. It quotes GMX and
 * Hyperliquid on every order and sends the fill to whichever venue is cheaper
 * for that size.
 *
 * The venue copy describes each venue's mechanism, which is stable. The quote
 * table is illustrative — see `ROUTE_EXAMPLE` in `landingContent.ts`.
 */
export function RoutingSection() {
  return (
    <section id="routing" className="relative overflow-hidden border-b" style={{ borderColor: "var(--line)" }}>
      <div
        className="pointer-events-none absolute h-[380px] w-[520px]"
        style={{
          top: "12%",
          right: "-12%",
          background: "radial-gradient(circle, var(--glow-a), transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="l-container relative py-88">
        <div data-reveal className="mb-44 text-center">
          <p className="l-eyebrow">Best execution</p>
          <h2 className="l-h2" style={{ maxWidth: "22ch", marginInline: "auto" }}>
            Two venues. One order. The better fill.
          </h2>
          <p className="l-intro" style={{ maxWidth: "62ch", marginInline: "auto" }}>
            USDX does not run its own book. Every order is quoted against GMX and Hyperliquid at the same instant, and
            the fill is routed to whichever venue is cheaper for that size — spread, fee and price impact included.
          </p>
        </div>

        <div data-reveal className="l-venues mb-24 gap-16">
          {VENUES.map((venue) => (
            <div key={venue.key} className="l-card l-card-hover flex flex-col gap-16 p-26">
              <div className="flex items-center gap-12">
                <span
                  className="grid h-40 w-40 flex-none place-items-center rounded-full text-16 font-extrabold"
                  style={{ background: venue.markBg, color: venue.markInk, fontFamily: "var(--font-display)" }}
                >
                  {venue.mark}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span
                    className="text-[19px] font-bold"
                    style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}
                  >
                    {venue.name}
                  </span>
                  <span className="text-12" style={{ color: "var(--ink-subtle)" }}>
                    {venue.kind} · {venue.chain}
                  </span>
                </div>
                <div className="flex-1" />
                <span className="l-live-chip flex-none">
                  <span className="l-dot" />
                  Routing
                </span>
              </div>

              <p className="text-[14.5px]" style={{ margin: 0, color: "var(--ink-muted)", textWrap: "pretty" }}>
                {venue.body}
              </p>

              <div className="mt-auto flex flex-wrap gap-7">
                {venue.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full px-11 py-5 text-11 font-semibold"
                    style={{ background: "var(--sunken)", color: "var(--ink-soft)" }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          data-reveal
          className="overflow-hidden rounded-16 border"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
        >
          <div
            className="flex flex-wrap items-center gap-x-14 gap-y-6 border-b px-22 py-14"
            style={{ background: "var(--surface-muted)", borderColor: "var(--line)" }}
          >
            <span className="l-micro" style={{ letterSpacing: "0.09em" }}>
              Example route
            </span>
            <span className="l-mono l-nums text-[12.5px]" style={{ color: "var(--ink-soft)" }}>
              {ROUTE_EXAMPLE.order}
            </span>
          </div>

          {/* The table is wider than a phone; it scrolls inside its own box
              rather than pushing the page sideways. */}
          <div className="overflow-x-auto">
            <table className="l-quote-table w-full" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {QUOTE_COLUMNS.map((column, index) => (
                    <th key={column} className={index === 0 ? "text-left" : "text-right"}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROUTE_EXAMPLE.quotes.map((quote) => (
                  <tr key={quote.venue} data-best={quote.best ? "" : undefined}>
                    <td className="text-left">
                      <span className="flex items-center gap-9">
                        <span className="font-semibold">{quote.venue}</span>
                        {quote.best && (
                          <span
                            className="rounded-full px-9 py-3 text-[10px] font-bold uppercase"
                            style={{
                              background: "var(--success-soft)",
                              color: "var(--success)",
                              letterSpacing: "0.08em",
                            }}
                          >
                            Best
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="l-nums text-right">{quote.entry}</td>
                    <td className="l-nums text-right">{quote.fee}</td>
                    <td className="l-nums text-right">{quote.impact}</td>
                    <td className="l-nums text-right font-semibold">{quote.net}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className="flex flex-wrap items-center gap-10 border-t px-22 py-16"
            style={{ borderColor: "var(--line)" }}
          >
            <span
              className="grid h-20 w-20 flex-none place-items-center rounded-full text-11 font-bold text-white"
              style={{ background: "var(--success)" }}
            >
              ✓
            </span>
            <span className="text-[14.5px] font-semibold" style={{ textWrap: "pretty" }}>
              {ROUTE_EXAMPLE.verdict}
            </span>
          </div>
        </div>

        <p data-reveal className="mt-14 text-center text-12" style={{ color: "var(--ink-subtle)" }}>
          Illustrative quotes. The router re-scores both venues on every order, so the winner changes with size and
          market conditions.
        </p>
      </div>
    </section>
  );
}
